import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { BookOpen, Flower2, Loader2, Lock } from 'lucide-react';
import { getApiBase, getBackendBaseUrl } from '../utils/apiBase';
import { resolveAssetUrl } from '../utils/resolveAssetUrl';
import { BrandWordmark } from '../components/brand/BrandWordmark';
import { MEMORIAL_COPY } from '../utils/memorial';
import { formatPersonDateRange } from '../utils/dateDisplay';
import { PublicLayout } from '../components/layout/PublicLayout';

const API = getApiBase();
const BACKEND = getBackendBaseUrl();

export default function PublishedTributeView() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const storyFocus = searchParams.get('story');
  const view = searchParams.get('view');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/public/remember/${slug}`);
        if (!cancelled) setData(res.data);
      } catch {
        if (!cancelled) setError('This tribute page is not available or is private.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[hsl(42_28%_98%)]">
        <Loader2 className="h-8 w-8 animate-spin text-[#8b7355]" aria-label="Loading tribute page" />
      </div>
    );
  }

  if (error || !data?.person) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-[hsl(42_28%_98%)] px-6 text-center">
        <Lock className="h-10 w-10 text-muted-foreground" aria-hidden />
        <p className="font-serif text-xl text-[hsl(24_14%_18%)]">{error || 'Not found'}</p>
        <Link to="/" className="text-sm text-[#8b7355] underline-offset-4 hover:underline">Return home</Link>
      </div>
    );
  }

  const person = data.person;
  const name = [person.given_names, person.surname].filter(Boolean).join(' ');
  const photo = person.profile_image_url ? resolveAssetUrl(BACKEND, person.profile_image_url) : null;
  const memories = data.memories || [];
  const featured = storyFocus ? memories.find((m) => m.id === storyFocus) : memories[0];

  return (
    <PublicLayout className="min-h-screen bg-gradient-to-b from-[hsl(42_30%_99%)] to-[hsl(38_18%_94%)]" footerVariant="compact">
      <header className="border-b border-[hsl(38_16%_88%)]/80 bg-white/70 backdrop-blur-sm">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-4 px-4 py-4 sm:px-6">
          <BrandWordmark className="h-7" />
          <span className="rounded-full border border-[hsl(38_16%_86%)] px-3 py-1 text-[10px] font-medium uppercase tracking-widest text-muted-foreground">
            {MEMORIAL_COPY.tributePage}
          </span>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-10 sm:px-6 sm:py-14">
        <div className="text-center">
          <Flower2 className="mx-auto h-8 w-8 text-[hsl(38_36%_42%)]/70" strokeWidth={1.25} aria-hidden />
          <p className="mt-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            {MEMORIAL_COPY.rememberedHere}
          </p>
          {photo ? (
            <img src={photo} alt="" className="mx-auto mt-6 h-32 w-32 rounded-full border border-[hsl(38_14%_86%)] object-cover shadow-sm" />
          ) : null}
          <h1 className="mt-6 font-serif text-3xl font-light text-[hsl(24_14%_16%)]">{name}</h1>
          {(person.birth_date || person.death_date) ? (
            <p className="mt-2 text-sm tabular-nums text-muted-foreground">
              {formatPersonDateRange(person)}
            </p>
          ) : null}
          {person.remembrance_line ? (
            <p className="mx-auto mt-6 max-w-lg font-serif text-lg italic leading-relaxed text-[hsl(32_10%_38%)]">
              &ldquo;{person.remembrance_line}&rdquo;
            </p>
          ) : null}
        </div>

        {view === 'audio' ? (
          <section className="mt-12 rounded-2xl border border-[hsl(38_14%_86%)] bg-white/80 p-6 text-center">
            <p className="font-serif text-lg text-[hsl(24_14%_18%)]">{MEMORIAL_COPY.audioKeepsake}</p>
            <p className="mt-2 text-sm text-muted-foreground">Public audio playback will expand in a later release. Family can listen in the private archive today.</p>
          </section>
        ) : null}

        {view === 'book' ? (
          <section className="mt-12 rounded-2xl border border-[hsl(38_14%_86%)] bg-white/80 p-6 text-center">
            <BookOpen className="mx-auto h-8 w-8 text-[#8B7355]/80" aria-hidden />
            <p className="mt-3 font-serif text-lg">Tribute book</p>
            <p className="mt-2 text-sm text-muted-foreground">Printed life book export arrives in Phase 1c. Your stories remain preserved in the family archive.</p>
          </section>
        ) : null}

        {data.biography ? (
          <section className="mt-12">
            <h2 className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Life story</h2>
            <p className="mt-4 font-serif text-base leading-relaxed text-[hsl(32_10%_34%)] whitespace-pre-wrap">{data.biography}</p>
          </section>
        ) : null}

        {memories.length > 0 && !view ? (
          <section className="mt-12 space-y-6">
            <h2 className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Memories</h2>
            {(featured ? [featured, ...memories.filter((m) => m.id !== featured.id)] : memories).map((m) => (
              <article key={m.id} className="rounded-2xl border border-[hsl(38_14%_86%)] bg-white/85 p-5 shadow-sm">
                <h3 className="font-serif text-lg text-[hsl(24_14%_16%)]">{m.title}</h3>
                {m.source_date ? <p className="mt-1 text-xs text-muted-foreground">{m.source_date}</p> : null}
                {m.excerpt ? <p className="mt-3 text-sm leading-relaxed text-[hsl(32_10%_38%)] whitespace-pre-wrap">{m.excerpt}</p> : null}
                {m.cover_image_url ? (
                  <img src={resolveAssetUrl(BACKEND, m.cover_image_url)} alt="" className="mt-4 max-h-48 w-full rounded-xl object-cover" loading="lazy" />
                ) : null}
              </article>
            ))}
          </section>
        ) : null}

        {data.timeline?.length ? (
          <section className="mt-12">
            <h2 className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Tribute timeline</h2>
            <ul className="mt-4 space-y-3 border-l border-[hsl(38_14%_82%)] pl-4">
              {data.timeline.map((ev, i) => (
                <li key={`${ev.kind}-${i}`} className="text-sm">
                  <span className="font-medium text-[hsl(24_14%_18%)]">{ev.label}</span>
                  {ev.date ? <span className="ml-2 text-muted-foreground tabular-nums">{ev.date}</span> : null}
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        <p className="mt-16 text-center text-[11px] text-muted-foreground">
          This tribute is linked from an external memorial keepsake. Content is shared at the family&apos;s discretion.
        </p>
      </main>
    </PublicLayout>
  );
}
